import { MMKV } from 'react-native-mmkv';
import type { SupportSyncPayload } from '@/services/diagnostics/supportSyncSummary';

export type PostUpdatePlatform = 'web' | 'android' | 'ios';

export interface PostUpdateIdentity {
  companyId: string;
  userId: string;
  deviceId: string;
  platform: PostUpdatePlatform;
}

export type PostUpdateSyncOutcome = 'success' | 'partial' | 'error';

export interface PostUpdateSyncAttempt {
  identity: PostUpdateIdentity;
  observedBuild: number;
  nativeBuild: number | null;
  compatibilityBuild: number;
  appVersion: string;
  traceId: string;
  startedAt: string;
  finishedAt: string;
  outcome: PostUpdateSyncOutcome;
}

export interface PreparedPostUpdateSummary {
  identity: PostUpdateIdentity;
  requestId: string;
  clientBuild: number;
  payloadSha512: string;
  summary: Record<string, unknown>;
  preparedAt: string;
  compatiblePayload?: SupportSyncPayload;
}

export type PostUpdateStage = 'sync' | 'diagnostic' | 'full_state';

export type PostUpdateReceiptStage = 'diagnostic' | 'full_state';
export interface PostUpdatePresentation {
  identity: PostUpdateIdentity;
  observedBuild: number;
  batchId: string;
  expiresAt: string | null;
  stages: Record<PostUpdateReceiptStage, {
    state: 'pending' | 'received';
    requestId: string | null;
    receivedAt?: string;
  }>;
}

export interface PersistedPostUpdateLease {
  identity: PostUpdateIdentity;
  batchId: string;
  stage: PostUpdateStage;
  requestId: string;
  leaseToken: string;
  leaseExpiresAt: string | null;
  attemptNo: number | null;
  savedAt: string;
}

const STORAGE_ID = 'app-guincho-post-update-collection-v1';
const ATTEMPT_PREFIX = 'attempt:';
const ATTEMPT_INDEX_PREFIX = 'attempt-index:';
const SUMMARY_PREFIX = 'summary:';
const LEASE_PREFIX = 'lease:';
const CAPABILITY_KEY = 'capability:post_update_v1';

type StringStorage = Pick<MMKV, 'getString' | 'set' | 'delete'>;

const encode = (value: string): string => encodeURIComponent(value);
const identityKey = (identity: PostUpdateIdentity): string => [
  identity.companyId,
  identity.userId,
  identity.deviceId,
  identity.platform,
].map(encode).join(':');

const keyForAttempt = (identity: PostUpdateIdentity, observedBuild: number): string =>
  `${ATTEMPT_PREFIX}${identityKey(identity)}:${observedBuild}`;

const keyForAttemptIndex = (identity: PostUpdateIdentity): string =>
  `${ATTEMPT_INDEX_PREFIX}${identityKey(identity)}`;

const keyForSummary = (identity: PostUpdateIdentity, requestId: string): string =>
  `${SUMMARY_PREFIX}${identityKey(identity)}:${encode(requestId)}`;

const keyForLease = (identity: PostUpdateIdentity, batchId: string, stage: PostUpdateStage): string =>
  `${LEASE_PREFIX}${identityKey(identity)}:${encode(batchId)}:${stage}`;

const parse = <T>(storage: StringStorage, key: string): T | null => {
  const raw = storage.getString(key);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    storage.delete(key);
    return null;
  }
};

const isIdentity = (value: unknown, expected: PostUpdateIdentity): boolean => {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<PostUpdateIdentity>;
  return candidate.companyId === expected.companyId
    && candidate.userId === expected.userId
    && candidate.deviceId === expected.deviceId
    && candidate.platform === expected.platform;
};

const isValidOutcome = (value: unknown): value is PostUpdateSyncOutcome =>
  value === 'success' || value === 'partial' || value === 'error';

const isValidAttempt = (value: unknown, identity: PostUpdateIdentity, observedBuild: number): value is PostUpdateSyncAttempt => {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<PostUpdateSyncAttempt>;
  return isIdentity(candidate.identity, identity)
    && candidate.observedBuild === observedBuild
    && typeof candidate.compatibilityBuild === 'number'
    && typeof candidate.appVersion === 'string'
    && typeof candidate.traceId === 'string'
    && typeof candidate.startedAt === 'string'
    && typeof candidate.finishedAt === 'string'
    && isValidOutcome(candidate.outcome);
};

const isValidSummary = (value: unknown, identity: PostUpdateIdentity, requestId: string): value is PreparedPostUpdateSummary => {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<PreparedPostUpdateSummary>;
  return isIdentity(candidate.identity, identity)
    && candidate.requestId === requestId
    && typeof candidate.clientBuild === 'number'
    && typeof candidate.payloadSha512 === 'string'
    && Boolean(candidate.summary && typeof candidate.summary === 'object')
    && typeof candidate.preparedAt === 'string';
};

const isValidLease = (value: unknown, identity: PostUpdateIdentity, batchId: string, stage: PostUpdateStage): value is PersistedPostUpdateLease => {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<PersistedPostUpdateLease>;
  return isIdentity(candidate.identity, identity)
    && candidate.batchId === batchId
    && candidate.stage === stage
    && typeof candidate.requestId === 'string'
    && typeof candidate.leaseToken === 'string'
    && (candidate.leaseExpiresAt === null || typeof candidate.leaseExpiresAt === 'string')
    && typeof candidate.savedAt === 'string';
};

export class PostUpdateCollectionStore {
  private readonly presentationListeners = new Set<() => void>();
  constructor(private readonly storage: StringStorage = new MMKV({ id: STORAGE_ID })) {}

  // Esta projeção só informa a tela; nunca controla coletas, leases ou repetição de envios.
  getPresentation(identity: PostUpdateIdentity, observedBuild: number): PostUpdatePresentation | null {
    try {
      const value = parse<PostUpdatePresentation>(this.storage, `presentation:${identityKey(identity)}`);
      if (!value || !isIdentity(value.identity, identity) || value.observedBuild !== observedBuild ||
          typeof value.batchId !== 'string' || !value.batchId ||
          !['diagnostic', 'full_state'].every(stage => {
            const item = value.stages?.[stage as PostUpdateReceiptStage];
            return item && (item.state === 'pending' ||
              (item.state === 'received' && typeof item.requestId === 'string' && Boolean(item.requestId) && Boolean(item.receivedAt)));
          })) return null;
      if (value.expiresAt && Date.parse(value.expiresAt) <= Date.now() &&
          Object.values(value.stages).some(stage => stage.state !== 'received')) return null;
      return value;
    } catch { return null; }
  }

  observePresentationBatch(identity: PostUpdateIdentity, observedBuild: number, batchId: string, expiresAt: string | null): void {
    try {
      const latest = parse<PostUpdatePresentation>(this.storage, `presentation:${identityKey(identity)}`);
      if (latest && latest.observedBuild > observedBuild) return;
      const existing = this.getPresentation(identity, observedBuild);
      if (existing?.batchId === batchId || (expiresAt && Date.parse(expiresAt) <= Date.now())) return;
      this.savePresentation({ identity, observedBuild, batchId, expiresAt, stages: {
        diagnostic: { state: 'pending', requestId: null },
        full_state: { state: 'pending', requestId: null },
      } });
    } catch { /* A apresentação não pode interromper a coleta. */ }
  }

  observePresentationStage(identity: PostUpdateIdentity, observedBuild: number, batchId: string,
    stage: PostUpdateReceiptStage, requestId: string, received: boolean): void {
    try {
      const value = this.getPresentation(identity, observedBuild);
      if (!value || value.batchId !== batchId || !requestId) return;
      if (value.stages[stage].requestId && value.stages[stage].requestId !== requestId) return;
      // O recibo nominal já confirmado não é rebaixado por uma tentativa atrasada.
      if (value.stages[stage].state === 'received') return;
      value.stages[stage] = { state: received ? 'received' : 'pending', requestId,
        ...(received ? { receivedAt: new Date().toISOString() } : {}) };
      this.savePresentation(value);
    } catch { /* Persistência de interface não muda o resultado do envio. */ }
  }

  subscribePresentation(listener: () => void): () => void {
    this.presentationListeners.add(listener);
    return () => { this.presentationListeners.delete(listener); };
  }

  private savePresentation(value: PostUpdatePresentation): void {
    this.storage.set(`presentation:${identityKey(value.identity)}`, JSON.stringify(value));
    for (const listener of this.presentationListeners) {
      try { listener(); } catch { /* Um observador não bloqueia os demais nem a coleta. */ }
    }
  }

  getFirstSyncAttempt(identity: PostUpdateIdentity, observedBuild: number): PostUpdateSyncAttempt | null {
    const candidate = parse<unknown>(this.storage, keyForAttempt(identity, observedBuild));
    return isValidAttempt(candidate, identity, observedBuild) ? candidate : null;
  }

  saveFirstSyncAttempt(attempt: PostUpdateSyncAttempt): PostUpdateSyncAttempt {
    const existing = this.getFirstSyncAttempt(attempt.identity, attempt.observedBuild);
    if (existing) return existing;
    this.storage.set(keyForAttempt(attempt.identity, attempt.observedBuild), JSON.stringify(attempt));
    const indexed = parse<number[]>(this.storage, keyForAttemptIndex(attempt.identity)) ?? [];
    if (!indexed.includes(attempt.observedBuild)) {
      indexed.push(attempt.observedBuild);
      indexed.sort((left, right) => right - left);
      this.storage.set(keyForAttemptIndex(attempt.identity), JSON.stringify(indexed.slice(0, 12)));
    }
    return attempt;
  }

  getFirstSyncAttemptAtOrAbove(identity: PostUpdateIdentity, minimumBuild: number): PostUpdateSyncAttempt | null {
    const indexed = parse<number[]>(this.storage, keyForAttemptIndex(identity)) ?? [];
    for (const observedBuild of indexed.filter(build => build >= minimumBuild).sort((left, right) => right - left)) {
      const attempt = this.getFirstSyncAttempt(identity, observedBuild);
      if (attempt) return attempt;
    }
    return null;
  }

  getPreparedSummary(identity: PostUpdateIdentity, requestId: string): PreparedPostUpdateSummary | null {
    const candidate = parse<unknown>(this.storage, keyForSummary(identity, requestId));
    return isValidSummary(candidate, identity, requestId) ? candidate : null;
  }

  savePreparedSummary(summary: PreparedPostUpdateSummary): void {
    this.storage.set(keyForSummary(summary.identity, summary.requestId), JSON.stringify(summary));
  }

  clearPreparedSummary(identity: PostUpdateIdentity, requestId: string): void {
    this.storage.delete(keyForSummary(identity, requestId));
  }

  getLease(identity: PostUpdateIdentity, batchId: string, stage: PostUpdateStage): PersistedPostUpdateLease | null {
    const candidate = parse<unknown>(this.storage, keyForLease(identity, batchId, stage));
    return isValidLease(candidate, identity, batchId, stage) ? candidate : null;
  }

  saveLease(lease: PersistedPostUpdateLease): void {
    this.storage.set(keyForLease(lease.identity, lease.batchId, lease.stage), JSON.stringify(lease));
  }

  clearLease(identity: PostUpdateIdentity, batchId: string, stage: PostUpdateStage): void {
    this.storage.delete(keyForLease(identity, batchId, stage));
  }

  markCapabilityUnavailable(until: number): void {
    this.storage.set(CAPABILITY_KEY, String(Math.max(0, Math.floor(until))));
  }

  isCapabilityUnavailable(now = Date.now()): boolean {
    const until = Number(this.storage.getString(CAPABILITY_KEY) ?? 0);
    return Number.isFinite(until) && until > now;
  }

  clearCapabilityUnavailable(): void {
    this.storage.delete(CAPABILITY_KEY);
  }
}

export const postUpdateCollectionStore = new PostUpdateCollectionStore();

/** Substitui o estado singleton somente nos testes; o runtime mantém a instância acima. */
export const __resetPostUpdateCollectionStoreForTests = (): void => {
  postUpdateCollectionStore.clearCapabilityUnavailable();
};

export const getPostUpdateIdentityKey = identityKey;
